import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { catchError, concatMap, filter, map, Observable, of, switchMap, tap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { PageInfo, ProjectSettingsInfo } from '@blaze-shared/api-contracts-domain';
import { appStateActions, fromRouter } from '@blaze-shared/app-state-data-access';
import { DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { blazePageActions } from '@blaze-shared/projects-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { getSchemaDatasetId } from '@blaze-shared/schema-domain';
import { Connection, Workflow } from '@blaze-shared/workflow-domain';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { LayoutsApiDataResponse, LayoutsApiService } from '../../data/layouts-api.service';
import { getElementDataSource } from '../../services/element-data-source.service';
import { PageGenerationService } from '../../services/page-pregeneration.service';
import { PregenerationUtilsService } from '../../services/pregeneration-utils.service';
import { getElementById, updateElementPropsBatch } from '../elements';

import {
  createRowActionPage,
  createRowActionPageFailure,
  createRowActionPageSuccess,
  tryCreateRowActionPage,
} from './row-action-page-generation.actions';
import { RowActionPageGenerationFacade } from './row-action-page-generation.facade';
import { CreatePageDto, RowActionPageConfig } from './row-action-page-generation.models';

@Injectable()
export class RowActionPageGenerationEffects {
  private readonly actions$ = inject(Actions);
  private readonly layoutsApi = inject(LayoutsApiService);
  private readonly pageGenerationService = inject(PageGenerationService);
  private readonly pregenerationUtilsService = inject(PregenerationUtilsService);
  private readonly rowActionPageGenerationFacade = inject(RowActionPageGenerationFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  updateProjectSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appStateActions.updateProjectSettings),
        map(({ settings }) => settings),
        filter((settings): settings is ProjectSettingsInfo => 'editOrViewDetailsAs' in settings),
        tap(({ editOrViewDetailsAs }) => {
          this.rowActionPageGenerationFacade.setDefaultPageType(editOrViewDetailsAs);
        }),
      ),
    { dispatch: false },
  );

  tryCreateRowActionPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tryCreateRowActionPage),
      concatLatestFrom(({ elementId }) => [
        this.store.select(getElementById(elementId)).pipe(filter(Boolean)),
        this.store.select(runtimeSelectors.selectPages),
        this.store.select(runtimeSelectors.selectWorkflows),
      ]),
      concatMap(([action, element, pages, workflows]) =>
        this.processTryCreateRowActionPage(action, element, pages, workflows as Workflow[]),
      ),
    ),
  );

  createRowActionPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createRowActionPage),
      concatLatestFrom(({ elementId }) => [
        this.store.select(getElementById(elementId)).pipe(filter(Boolean)),
        this.store.select(fromRouter.getProjectId).pipe(filter(Boolean)),
        this.store.select(runtimeSelectors.selectPages),
      ]),
      concatMap(([action, element, projectId, pages]) =>
        this.processCreateRowActionPage(action, element, projectId, pages),
      ),
    ),
  );

  private processTryCreateRowActionPage(
    action: ReturnType<typeof tryCreateRowActionPage>,
    element: CanvasElement,
    pages: PageInfo[],
    workflows: Workflow[],
  ): Observable<Action> {
    const { actionPath, pregenerationType, pageType, forceCreate } = action;
    const dialogFormActionPath = forceCreate ? undefined : actionPath;

    return this.pregenerationUtilsService
      .tryUpdateDialogFormSchema(element, dialogFormActionPath, pages, workflows)
      .pipe(
        filter(Boolean),
        map(() => {
          const name = this.pregenerationUtilsService.getNewDialogName(
            element,
            pregenerationType,
            pages,
            workflows,
          );

          return createRowActionPage({
            name,
            elementId: element.id,
            actionPath,
            pregenerationType,
            pageType,
          });
        }),
      );
  }

  private processCreateRowActionPage(
    action: ReturnType<typeof createRowActionPage>,
    element: CanvasElement,
    projectId: string,
    pages: PageInfo[],
  ): Observable<Action> {
    const elementDataSource = hasDataProps(element)
      ? getElementDataSource('value', element.props.data)
      : undefined;

    if (!elementDataSource) {
      return of(
        createRowActionPageFailure({
          elementId: element.id,
          pageType: action.pageType,
          pregenerationType: action.pregenerationType,
          actionPath: action.actionPath,
          error: `Dialog generation failed: no datasource bound`,
        }),
      );
    }

    const mainDatasetId = this.getDatasetId(elementDataSource);

    return this.createAndPregeneratePage({
      datasetId: mainDatasetId,
      dataSource: elementDataSource,
      name: action.name,
      pages,
      pageType: action.pageType,
      pregenerationType: action.pregenerationType,
      projectId,
    }).pipe(
      concatMap(({ page, layoutContent, connections }) =>
        this.updateLayout(layoutContent, connections, page.id).pipe(
          switchMap(() => this.buildSuccessActions(element.id, action, page)),
        ),
      ),
      catchError((error) =>
        of(
          createRowActionPageFailure({
            elementId: element.id,
            pageType: action.pageType,
            pregenerationType: action.pregenerationType,
            actionPath: action.actionPath,
            error:
              error instanceof Error ? error.message : 'There was an error while creating a page',
          }),
        ),
      ),
    );
  }

  private createAndPregeneratePage(params: CreatePageDto): Observable<{
    layoutContent: CanvasElement[];
    connections: Connection[];
    page: PageInfo;
  }> {
    const { datasetId, dataSource, name, pages, pageType, pregenerationType, projectId } = params;

    return this.pageGenerationService
      .createPage(
        {
          projectId,
          name,
          pageType,
          isHomePage: false,
          mainDatasetId: datasetId,
        },
        pages,
      )
      .pipe(
        concatMap((page) =>
          this.pageGenerationService
            .regeneratePrefilledPageLayoutMutably(
              page.id,
              projectId,
              pregenerationType,
              pageType,
              dataSource,
              { formTitle: name },
            )
            .pipe(
              map(({ layoutContent, connections }) => ({
                layoutContent,
                connections: connections ?? [],
                page,
              })),
            ),
        ),
      );
  }

  private updateLayout(
    content: CanvasElement[],
    connections: Connection[],
    pageId: string,
  ): Observable<LayoutsApiDataResponse[]> {
    return this.layoutsApi.updateLayouts([
      {
        pageId,
        version: 0,
        uiLayer: { content },
        actionLayer: { connections },
      },
    ]);
  }

  private buildSuccessActions(
    elementId: CanvasElementId,
    config: RowActionPageConfig & { name: string },
    page: PageInfo,
  ): Action[] {
    const { name, pregenerationType, pageType, actionPath } = config;

    const actions: Action[] = [
      createRowActionPageSuccess({
        name,
        elementId,
        pregenerationType,
        pageType,
        actionPath,
      }),
      blazePageActions.addOrUpdatePage({ page }),
    ];

    if (actionPath) {
      actions.push(
        updateElementPropsBatch({
          elementId,
          updates: [
            { path: `${actionPath}.targetId`, value: page.id },
            { path: `${actionPath}.targetType`, value: page.pageType },
            { path: `${actionPath}.enabled`, value: true },
          ],
        }),
      );
    }

    return actions;
  }

  private getDatasetId(dataSource: DataBindingSourceInfo): string | undefined {
    if (dataSource.sourceType === 'dataset') {
      return dataSource.id;
    }
    return getSchemaDatasetId(this.schemaService.getWorkflowSchema(dataSource.id));
  }
}
